import React from 'react'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import * as RegularIcons from '@fortawesome/free-regular-svg-icons'
import * as SolidIcons from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as TrashSvg } from '../../assets/icons/trash.svg'
import { ReactComponent as PenSvg } from '../../assets/icons/pen.svg'
import { ReactComponent as EyeSvg } from '../../assets/icons/eye.svg'
import { ReactComponent as ProcessSvg } from '../../assets/icons/process.svg'
import { ReactComponent as CopySvg } from '../../assets/icons/copy.svg'
import { ReactComponent as ZoomInSvg } from '../../assets/icons/zoomIn.svg'
import { ReactComponent as ZoomOutSvg } from '../../assets/icons/zoomOut.svg'
import { ReactComponent as ExpandSvg } from '../../assets/icons/expand.svg'
import { ReactComponent as QuestionSvg } from '../../assets/icons/question.svg'
import { ReactComponent as CheckCircleSvg } from '../../assets/icons/checkCircle.svg'
import { ReactComponent as CircleQuestionThickSvg } from '../../assets/icons/circleQuestionThick.svg'
import { ReactComponent as CircleMinusThickSvg } from '../../assets/icons/circleMinusThick.svg'
import { ReactComponent as CirclePlusThickSvg } from '../../assets/icons/circlePlusThick.svg'
import { ReactComponent as ShareThickSvg } from '../../assets/icons/shareThick.svg'
import { ReactComponent as CheckmarkCircleThickSvg } from '../../assets/icons/checkmarkCircleThick.svg'
import { ReactComponent as ChevronDownSvg } from '../../assets/icons/chevronDownSymbol.svg'
import { ReactComponent as ChevronUpSvg } from '../../assets/icons/chevronUpSymbol.svg'
import { ReactComponent as PlusCircleSvg } from '../../assets/icons/plusCircle.svg'
import { ReactComponent as MinusCircleSvg } from '../../assets/icons/minusCircle.svg'
import { ReactComponent as XCircleSvg } from '../../assets/icons/xCircle.svg'
import { ReactComponent as CornerDownRight } from '../../assets/icons/cornerDownRight.svg'
import theme from '../../styles/theme'

// Type definitions
export type IconSize =
  | 'xxs'
  | 'xs'
  | 'extraSmall'
  | 'small'
  | 's'
  | 'medium'
  | 'm'
  | 'large'
  | 'l'

export interface BaseIconProps {
  size?: IconSize
  color?: string
  hoverColor?: string
  onClick?: (event?: React.MouseEvent) => void
  disabled?: boolean
  clickDisabled?: boolean
  'data-testid'?: string
}

export interface FontAwesomeIconProps extends BaseIconProps {
  solid?: boolean
  noHover?: boolean
}

// Size mapping for icons
const sizes: Record<IconSize, string> = {
  xxs: '10px',
  xs: '16px',
  extraSmall: '16px',
  small: '20px',
  s: '20px',
  medium: '24px',
  m: '24px',
  large: '29px',
  l: '29px',
}

// Function to lighten a color by percentage
const lightenColor = (color: string, percent: number): string => {
  // Remove any leading #
  const hex = color.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  // Lighten each component
  const lightenComponent = (c: number) => {
    const amount = Math.floor((255 - c) * (percent / 100))

    return Math.min(255, c + amount)
  }

  const lightR = lightenComponent(r)
  const lightG = lightenComponent(g)
  const lightB = lightenComponent(b)

  // Convert back to hex
  const toHex = (c: number) => {
    const hex = c.toString(16)

    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(lightR)}${toHex(lightG)}${toHex(lightB)}`
}

interface StyledIconProps {
  size?: IconSize
  color?: string
  hoverColor?: string
  disabled?: boolean
  clickDisabled?: boolean
  'data-testid'?: string
}

// Base styled component for SVG icons
const StyledIcon = styled.div<StyledIconProps>`
  cursor: ${(props) =>
    props.clickDisabled
      ? 'default'
      : props.disabled
        ? 'not-allowed'
        : 'pointer'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  margin: -0.5rem;
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: transform 0.1s ease;

  svg {
    width: ${(props) => sizes[props.size || 'medium']};
    height: ${(props) => sizes[props.size || 'medium']};

    path,
    polygon {
      stroke: ${(props) => props.color || theme.colors.standardText};
      transition: stroke 0.2s ease;
    }
  }

  &:hover path,
  &:hover polygon {
    stroke: ${(props) => {
      if (props.disabled || props.clickDisabled)
        return props.color || theme.colors.standardText
      if (props.hoverColor) return props.hoverColor
      return (
        !props.disabled &&
        !props.clickDisabled &&
        (props.color
          ? lightenColor(props.color, 20)
          : theme.colors.veryFadedText)
      )
    }};
  }

  &:active {
    transform: ${(props) =>
      !props.disabled && !props.clickDisabled ? 'scale(0.95)' : 'none'};

    path,
    polygon {
      stroke: ${(props) =>
        !props.disabled &&
        !props.clickDisabled &&
        (props.color ? lightenColor(props.color, 35) : theme.colors.fadedText)};
    }
  }

  &:hover {
    cursor: ${(props) =>
      props.clickDisabled
        ? 'default'
        : props.disabled
          ? 'not-allowed'
          : 'pointer'};
  }
`

interface StyledFontAwesomeIconProps extends StyledIconProps {
  noHover?: boolean
  'data-testid'?: string
}

// Styled component for FontAwesome icons
const StyledFontAwesomeIcon = styled.div<StyledFontAwesomeIconProps>`
  cursor: ${(props) =>
    props.clickDisabled
      ? 'default'
      : props.disabled
        ? 'not-allowed'
        : 'pointer'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  margin: -0.5rem;
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  svg {
    width: ${(props) => sizes[props.size || 'medium']};
    height: ${(props) => sizes[props.size || 'medium']};
    color: ${(props) => props.color || theme.colors.standardText};
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: ${(props) => {
      if (props.disabled || props.noHover || props.clickDisabled)
        return props.color || theme.colors.standardText
      if (props.hoverColor) return props.hoverColor
      return props.color
        ? lightenColor(props.color, 20)
        : theme.colors.veryFadedText
    }};
  }

  &:hover {
    cursor: ${(props) =>
      props.clickDisabled
        ? 'default'
        : props.disabled
          ? 'not-allowed'
          : 'pointer'};
  }
`

// React components for custom SVG icons
const createSvgIcon =
  (SvgComponent: React.ComponentType) =>
  ({
    size = 'medium',
    color,
    hoverColor,
    onClick,
    disabled,
    clickDisabled,
    'data-testid': dataTestId,
  }: BaseIconProps) => (
    <StyledIcon
      size={size}
      color={color}
      hoverColor={hoverColor}
      onClick={
        disabled || clickDisabled
          ? undefined
          : (e: React.MouseEvent) => onClick?.(e)
      }
      disabled={disabled}
      clickDisabled={clickDisabled}
      data-testid={dataTestId}
    >
      <SvgComponent />
    </StyledIcon>
  )

// Base exports object with our custom SVG icons
const baseExports = {
  TrashIcon: createSvgIcon(TrashSvg),
  PenIcon: createSvgIcon(PenSvg),
  EyeIcon: createSvgIcon(EyeSvg),
  ProcessIcon: createSvgIcon(ProcessSvg),
  CopyIcon: createSvgIcon(CopySvg),
  ZoomInIcon: createSvgIcon(ZoomInSvg),
  ZoomOutIcon: createSvgIcon(ZoomOutSvg),
  ExpandIcon: createSvgIcon(ExpandSvg),
  QuestionIcon: createSvgIcon(QuestionSvg),
  CheckCircleIcon: createSvgIcon(CheckCircleSvg),
  CircleQuestionThickIcon: createSvgIcon(CircleQuestionThickSvg),
  CircleMinusThickIcon: createSvgIcon(CircleMinusThickSvg),
  CirclePlusThickIcon: createSvgIcon(CirclePlusThickSvg),
  ShareThickIcon: createSvgIcon(ShareThickSvg),
  CheckmarkCircleThickIcon: createSvgIcon(CheckmarkCircleThickSvg),
  ChevronDownIcon: createSvgIcon(ChevronDownSvg),
  ChevronUpIcon: createSvgIcon(ChevronUpSvg),
  PlusCircleIcon: createSvgIcon(PlusCircleSvg),
  MinusCircleIcon: createSvgIcon(MinusCircleSvg),
  XCircleIcon: createSvgIcon(XCircleSvg),
  CornerDownRight: createSvgIcon(CornerDownRight),
  createIcon: (faIcon: IconDefinition, fromSolid = false) => {
    const WrappedIcon = ({
      size = 'medium',
      color,
      hoverColor,
      onClick,
      disabled,
      clickDisabled,
      solid = fromSolid,
      'data-testid': dataTestId,
    }: FontAwesomeIconProps) => (
      <StyledFontAwesomeIcon
        size={size}
        color={color}
        hoverColor={hoverColor}
        onClick={
          disabled || clickDisabled
            ? undefined
            : (e: React.MouseEvent) => onClick?.(e)
        }
        disabled={disabled}
        clickDisabled={clickDisabled}
        data-testid={dataTestId}
      >
        <FontAwesomeIcon icon={faIcon} />
      </StyledFontAwesomeIcon>
    )

    return WrappedIcon
  },
}

// Type for the icons proxy
type CustomIconsType = typeof baseExports & {
  [K in
    | keyof typeof SolidIcons
    | keyof typeof RegularIcons]: React.ComponentType<FontAwesomeIconProps>
}

// Proxy handler for FontAwesome icons
const iconHandler: ProxyHandler<typeof baseExports> = {
  get: function (target, prop: string | symbol) {
    if (typeof prop === 'string' && prop in target) {
      return target[prop as keyof typeof target]
    }

    if (typeof prop === 'string' && prop.startsWith('fa')) {
      // Check if we already cached this icon
      if (prop in target) {
        return target[prop as keyof typeof target]
      }

      const solidIcon = SolidIcons[
        prop as keyof typeof SolidIcons
      ] as IconDefinition
      const regularIcon = RegularIcons[
        prop as keyof typeof RegularIcons
      ] as IconDefinition

      if (solidIcon || regularIcon) {
        const WrappedIcon = ({
          size = 'medium',
          color,
          hoverColor,
          onClick,
          disabled,
          clickDisabled,
          solid = true,
          noHover = false,
          'data-testid': dataTestId,
        }: FontAwesomeIconProps) => (
          <StyledFontAwesomeIcon
            size={size}
            color={color}
            hoverColor={hoverColor}
            onClick={
              disabled || clickDisabled
                ? undefined
                : (e: React.MouseEvent) => onClick?.(e)
            }
            disabled={disabled}
            clickDisabled={clickDisabled}
            noHover={noHover}
            data-testid={dataTestId}
          >
            <FontAwesomeIcon
              icon={solid ? solidIcon : regularIcon || solidIcon}
            />
          </StyledFontAwesomeIcon>
        )

        // Cache the component
        ;(target as any)[prop] = WrappedIcon

        return WrappedIcon
      }
    }

    return undefined
  },
}

// Create and export the proxy
const CustomIcons = new Proxy(baseExports, iconHandler) as CustomIconsType

export default CustomIcons
